<template>
  <div class="onboarding">
    <CalicoGlobalNavigation />
    <div class="page">
      <CalicoProgressBar
        v-if="progresses.length && current && !loadingProgress"
        :current="current"
        :steps="progresses"
        @back="back"
      />
      <div class="content-container">
        <div class="content">
          <div
            v-if="error"
            class="banner-container"
          >
            <CalicoBanner
              variant="error"
              fixed
            >
              {{ error }}
            </CalicoBanner>
          </div>
          <CalicoLoader v-if="loading || loadingProgress" />
          <slot v-else />
        </div>
      </div>
    </div>
    <div
      v-if="footer"
      class="footer"
    >
      <label class="label--xsmall">Next step: {{ progresses[current + 1]?.name }}</label>
      <div style="width: fit-content;">
        <button
          class="button-primary label--large"
          :disabled="loading || loadingProgress"
          @click="save"
        >
          Save & Continue
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import CalicoBanner from "@/components/calico/banner/index.vue";
import CalicoProgressBar from "@/components/pattern/progressBar/index.vue";
import CalicoGlobalNavigation from "@/components/pattern/globalNavigation/index.vue";
import CalicoLoader from '@/components/calico/loader/index.vue';
import service from "@/services/index.js";
import { useForm } from 'vee-validate'

export default {
  name: "TemplateOnboarding",
  components: {
    CalicoProgressBar,
    CalicoBanner,
    CalicoGlobalNavigation,
    CalicoLoader
  },
  props: {
    type: {
      type: String,
      default: 'onboarding'
    },
    formData: {
      type: Object,
      default: () => ({})
    },
    error: {
      type: String,
      default: ''
    },
    footer: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const { validate, setValues } = useForm({
      initialValues: props.formData,
    });
    return { validate, setValues };
  },
  data() {
    return {
      loadingProgress: true,
      current: 0,
      progresses: []
    }
  },
  watch: {
    formData: {
      handler(newFormData) {
        this.setValues(newFormData, false)
      },
      deep: true
    }
  },
  async mounted() {
    this.progresses = await service.progress.get(this.type);
    this.current = this.progresses.findIndex((progress) => progress.route === location.pathname)
    this.loadingProgress = false
  },
  methods: {
    async save () {
      const result = await this.validate();
      if (!result.valid) return;
      
      const current = this.progresses[this.current]
      const next = this.progresses[this.current + 1]
      if (this.$attrs.onSave) {
        await new Promise((resolve) => this.$emit('save', resolve))
      }
      this.loadingProgress = true
      await service.progress.complete(current.id)
      if (this.current === (this.progresses.length - 2)) {
        await service.progress.complete(next.id)
      }
      if (next) {
        await this.$router.push(next.route)
        this.loadingProgress = false
      }
    },
    back () {
      this.loadingProgress = true
      this.$emit('back')
      if (this.progresses[this.current - 1]) {
        this.$router.push(this.progresses[this.current - 1].route)
      }
    },
  }
};
</script>

<style scoped lang="scss">
.onboarding {
  @include stack-space($space-none);
  background-color: $color-background-secondary;
  height: 100vh;
  width: 100%;
  align-items: center;
  overflow: auto;

  :deep(.calico-navbar) {
    .button-tertiary-icon {
      &:has(.bi-search) {
        display: none;
      }
    }
  }

  .page {
    @include stack-space($space-none);
    height: 100%;
    width: 100%;
    max-width: $large-screen-max-width;
    overflow: hidden;
    scrollbar-width: none;
    .content-container {
      display: flex;
      overflow: auto;
      justify-content: center;
      flex: 1;
      .content {
        .banner-container {
          display: flex;
          @include inset-space($space-large);
          justify-content: center;

          .calico-banner {
            max-width: $tablet-breakpoint-min-width;
          }
        }
        width: 100%;
        max-width: $tablet-breakpoint-min-width;
        flex: 1;
      }
    }
    .content-container::-webkit-scrollbar {
      display: none;
    }
  }

  .footer {
    @include stack-space($space-medium);
    @include inset-space($space-large);
    width: 100%;
    align-items: center;
    background: $color-white;
    label {
      color: $color-text-secondary;
      font-weight: $font-weight-bold;
    }
  }
}
</style>
